import React, { useState, useEffect } from 'react';
import AddJobModal from './AddJobModal';
import { apiFetch } from './utils/api';
import jobStatuses from './constants/job-statuses';

function CustomerJobsTab({ customerId, customerName }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [customerId]);

  const fetchJobs = async () => {
    try {
      const res = await apiFetch('/api/jobs');
      const allJobs = await res.json();
      
      // Filter jobs for this customer
      const customerJobs = allJobs.filter(job => job.customer_id === customerId);
      setJobs(customerJobs);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getStatusColor = (status) => jobStatuses.statusTailwind(status);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Jobs</h2>
          <p className="text-gray-600 mt-1">
            {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} for {customerName}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
        >
          ➕ Add a Job
        </button>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Jobs Yet</h3>
          <p className="text-gray-600 mb-4">
            Create your first job for {customerName}
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
          >
            ➕ Create First Job
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Job Number
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Description
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Site
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Status
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Category
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm font-medium text-gray-900">
                      {job.job_number}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {job.job_description || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">
                      {job.site_id ? `Site #${job.site_id}` : '🏢 Head Office'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.job_status)}`}>
                      <span aria-hidden="true">{jobStatuses.statusIcon(job.job_status)}</span>
                      {jobStatuses.labelFor(job.job_status)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600 capitalize">
                      {job.job_category || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Job Modal */}
      {showAddModal && (
        <AddJobModal
          customerId={customerId}
          customerName={customerName}
          onClose={() => setShowAddModal(false)}
          onJobCreated={(newJob) => {
            setJobs([newJob, ...jobs]);
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

export default CustomerJobsTab;
